import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats duration in seconds to a human-readable string
 * @param seconds - Duration in seconds
 * @param format - 'short' (e.g., "1h 30m") or 'long' (e.g., "1 hour 30 minutes")
 * @returns Formatted duration string
 */
export function formatDuration(seconds: number, format: 'short' | 'long' = 'short'): string {
  if (seconds === 0) return format === 'short' ? '0m' : '0 minutes';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(format === 'short' ? `${hours}h` : `${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  }
  if (minutes > 0) {
    parts.push(format === 'short' ? `${minutes}m` : `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  }
  if (secs > 0 && hours === 0) {
    parts.push(format === 'short' ? `${secs}s` : `${secs} ${secs === 1 ? 'second' : 'seconds'}`);
  }

  return parts.join(' ');
}

/**
 * Formats a date string to a human-readable format
 * @param dateString - ISO date string
 * @param format - 'short' (e.g., "Jan 1") or 'long' (e.g., "January 1, 2024")
 * @returns Formatted date string
 */
export function formatDate(dateString: string, format: 'short' | 'long' = 'long'): string {
  const date = new Date(dateString);

  if (format === 'short') {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
